import { useEffect, useMemo, useState } from 'react';
import { PiFacebookLogoDuotone, PiInstagramLogoDuotone, PiMapPinDuotone, PiMegaphoneDuotone, PiPlantDuotone } from 'react-icons/pi';
import { SiX } from 'react-icons/si';
import Colors, { withAlpha } from '../../constants/Colors';
import { firebaseAnalyticsApi, THEME_MODE, UserAttribution } from '../../services/api';

/** Same app key the analytics endpoints use - picks the user-preferences table. */
export const ATTRIBUTION_APP = THEME_MODE === 'dev' ? 'nonprod' : 'prod';

const LOOKUP_CHUNK = 500;

// Shared across every card on the page: id -> attribution / location (null = looked up, none found).
type PreferenceEntry = { attribution: UserAttribution | null; location: string | null };
const cache = new Map<string, PreferenceEntry>();
const inflight = new Set<string>();
const listeners = new Set<() => void>();
const cacheKey = (app: string, id: string) => `${app}|${id.trim().toLowerCase()}`;

/** Looks up install attribution and location for the given user ids (email / anonymous uuid /
 * Clerk id), batching ids not seen yet. Returns a getter; cards re-render when results arrive. */
function usePreferenceLookup(ids: Array<string | null | undefined>, app: string) {
  const [, setVersion] = useState(0);
  const unique = useMemo(
    () => Array.from(new Set(ids.map((id) => (id || '').trim()).filter(Boolean))).sort(),
    [ids]
  );
  const idsKey = unique.join('\n');

  useEffect(() => {
    const listener = () => setVersion((v) => v + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    const missing = unique.filter((id) => !cache.has(cacheKey(app, id)) && !inflight.has(cacheKey(app, id)));
    if (missing.length === 0) return;
    missing.forEach((id) => inflight.add(cacheKey(app, id)));
    for (let i = 0; i < missing.length; i += LOOKUP_CHUNK) {
      const chunk = missing.slice(i, i + LOOKUP_CHUNK);
      firebaseAnalyticsApi.getAttributions(chunk, app).then((response) => {
        chunk.forEach((id) => {
          inflight.delete(cacheKey(app, id));
          // On a failed request leave ids uncached so a later render retries them.
          if (response.success && response.data) {
            cache.set(cacheKey(app, id), {
              attribution: response.data.attributions[id] ?? null,
              location: response.data.locations?.[id] ?? null,
            });
          }
        });
        listeners.forEach((notify) => notify());
      });
    }
    // idsKey stands in for `unique` so a new array with the same ids doesn't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, app]);

  return (id: string | null | undefined): PreferenceEntry | null => (id && cache.get(cacheKey(app, id))) || null;
}

/** Install attribution getter for the given user ids. */
export function useAttributions(ids: Array<string | null | undefined>, app: string = ATTRIBUTION_APP) {
  const lookup = usePreferenceLookup(ids, app);
  return (id: string | null | undefined): UserAttribution | null => lookup(id)?.attribution ?? null;
}

/** preferences.location getter for the given user ids. Shares the attribution cache and request. */
export function useUserLocations(ids: Array<string | null | undefined>, app: string = ATTRIBUTION_APP) {
  const lookup = usePreferenceLookup(ids, app);
  return (id: string | null | undefined): string | null => lookup(id)?.location ?? null;
}

const PLATFORM_LABEL: Record<string, string> = { instagram: 'Instagram', facebook: 'Facebook', x: 'X' };
const PLATFORM_COLOR: Record<string, string> = { instagram: '#E1306C', facebook: '#1877F2', x: '#000000' };

const DETAIL_ROWS: Array<[keyof UserAttribution, string]> = [
  ['media_source', 'Media source'],
  ['af_channel', 'Channel'],
  ['campaign', 'Campaign'],
  ['af_adset', 'Ad set'],
  ['af_ad', 'Ad'],
  ['af_sub1', 'Sub1'],
  ['deep_link_value', 'Deep link'],
  ['is_retargeting', 'Retargeting'],
  ['click_time', 'Click time'],
  ['install_time', 'Install time'],
];

export function attributionLabel(attribution: UserAttribution): string {
  if (attribution.type === 'organic') return 'Organic';
  if (attribution.platform) return PLATFORM_LABEL[attribution.platform];
  return attribution.media_source || 'Non-organic';
}

/** Small round install-source icon (organic / Instagram / Facebook / X / other paid source)
 * with the AppsFlyer details in its tooltip. Renders nothing when there is no attribution. */
export default function AttributionBadge({ attribution, size = 22 }: { attribution: UserAttribution | null; size?: number }) {
  if (!attribution) return null;

  const organic = attribution.type === 'organic';
  const color = organic ? Colors.success : attribution.platform ? PLATFORM_COLOR[attribution.platform] : Colors.secondary;
  const iconSize = Math.round(size * 0.62);
  const icon = organic ? (
    <PiPlantDuotone size={iconSize} />
  ) : attribution.platform === 'instagram' ? (
    <PiInstagramLogoDuotone size={iconSize} />
  ) : attribution.platform === 'facebook' ? (
    <PiFacebookLogoDuotone size={iconSize} />
  ) : attribution.platform === 'x' ? (
    <SiX size={Math.round(iconSize * 0.85)} />
  ) : (
    <PiMegaphoneDuotone size={iconSize} />
  );

  const tooltip = [
    organic ? 'Organic install' : `Non-organic install · ${attributionLabel(attribution)}`,
    ...(organic ? [] : DETAIL_ROWS.filter(([field]) => attribution[field]).map(([field, label]) => `${label}: ${attribution[field]}`)),
    ...(organic && attribution.install_time ? [`Install time: ${attribution.install_time}`] : []),
  ].join('\n');

  return (
    <span
      title={tooltip}
      aria-label={tooltip.split('\n')[0]}
      role="img"
      style={{
        flex: 'none',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '999px',
        display: 'inline-grid',
        placeItems: 'center',
        color,
        background: withAlpha(color, 0.12),
        border: `1px solid ${withAlpha(color, 0.35)}`,
        cursor: 'help',
      }}
    >
      {icon}
    </span>
  );
}

/** Pin + location text from preferences.location; full value in the tooltip. Renders nothing when unknown. */
export function LocationTag({ location, maxWidth = 180 }: { location: string | null; maxWidth?: number | string }) {
  if (!location) return null;
  return (
    <span
      title={location}
      style={{
        flex: 'none',
        maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        fontSize: '11px',
        fontWeight: 600,
        color: Colors.textLight,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}
    >
      <PiMapPinDuotone size={13} style={{ flex: 'none' }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{location}</span>
    </span>
  );
}

/** Muted pastel of the theme blue for user cards (sync detection, free-trial logs). */
export const USER_CARD = {
  background: withAlpha(Colors.secondary, 0.13),
  border: withAlpha(Colors.secondary, 0.34),
  divider: withAlpha(Colors.secondary, 0.24),
};

/** User card footer: location on the left, install source on the right. Renders nothing when neither is known. */
export function UserCardFooter({ location, attribution }: { location: string | null; attribution: UserAttribution | null }) {
  if (!location && !attribution) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingTop: '10px', marginTop: 'auto', borderTop: `1px solid ${USER_CARD.divider}` }}>
      <span style={{ flex: 1, minWidth: 0, display: 'flex' }}>
        <LocationTag location={location} maxWidth="100%" />
      </span>
      <AttributionBadge attribution={attribution} size={24} />
    </div>
  );
}
